import { RichTextInput } from '@/components/RichTextInput/RichTextInput';
import { ActionIcon, Box, Card, Flex, Group, Loader, Text, ThemeIcon, Tooltip } from '@mantine/core';
import { formatHumanName, getReferenceString } from '@medplum/core';
import { IconNote, IconSend, IconX } from '@tabler/icons-react';
import { useMemo } from 'react';
import { usePostHog } from 'posthog-js/react';
import { trackMessageDraftAbandoned } from '@/services/analytics';
import { useSendStandaloneNote } from '@/hooks/useSendStandaloneNote';
import { getPatientName } from '@/pages/PatientTimelinePage/services/timelinePatient';
import { useTimelineContext } from '@/hooks/useTimelineContext';

export function StandaloneNoteMessageBox() {
  const { textareaInputRef, isSending, content, setContent, richTextContent, setRichTextContent, handleSendMessage } =
    useSendStandaloneNote();

  const { patientContext, careTeamContext, partnerContext, messageBoxContext } = useTimelineContext();

  const patient = patientContext?.patient;
  const organization = partnerContext?.organization;
  const isPartnerContext = !!partnerContext;

  const { setShowMessageInput } = messageBoxContext;
  const posthog = usePostHog();

  const { careTeamProviders } = careTeamContext ?? {};

  const patientName = useMemo(() => (patient ? getPatientName(patient) : 'Unknown Patient'), [patient]);
  const organizationName = useMemo(() => organization?.name || 'Unknown Partner', [organization]);
  const displayName = isPartnerContext ? organizationName : patientName;

  return (
    <Card withBorder padding={0} radius="lg" style={{ backgroundColor: '#fffbeb', borderColor: '#FCECCC' }}>
      <Box style={{ borderBottom: '1px solid #FCECCC' }}>
        <Group w="100%" p="xs" justify="space-between" align="center">
          <Group>
            <ThemeIcon variant="filled" radius="lg" size="lg" bg="#C6A467">
              <IconNote size={16} />
            </ThemeIcon>
            <Text variant="header" c="#C6A467">
              Add sticky note on {displayName}'s timeline
            </Text>
          </Group>
          <Tooltip label="Close" withArrow position="top-end" openDelay={500}>
            <ActionIcon
              variant="subtle"
              size="md"
              radius="xl"
              aria-label="Close"
              onClick={() => {
                if (content.trim()) {
                  trackMessageDraftAbandoned(posthog, {
                    message_type: 'note',
                    draft_length: content.length,
                    patient_id: patient?.id,
                    organization_id: organization?.id,
                  });
                }
                setShowMessageInput(false);
              }}
              style={{ color: '#C6A467' }}
            >
              <IconX size={16} />
            </ActionIcon>
          </Tooltip>
        </Group>
        <Box p="lg" style={{ borderTop: '1px solid #FCECCC' }}>
          <Flex gap="xs" align="center">
            <RichTextInput
              value={richTextContent}
              onChange={(text, json) => {
                setContent(text);
                setRichTextContent(json);
              }}
              textareaInputRef={textareaInputRef as React.RefObject<HTMLDivElement>}
              disabled={isSending}
              placeholder="Write a note..."
              mentionItems={(careTeamProviders ?? []).map((provider) => ({
                id: getReferenceString(provider) ?? '',
                label: formatHumanName(provider.name?.[0]),
              }))}
              editorKey={`${isPartnerContext ? organization?.id : patient?.id}-${(careTeamProviders ?? []).length}`}
            />
            <ActionIcon
              variant="filled"
              size="lg"
              radius="xl"
              c="white"
              aria-label="Send"
              onClick={handleSendMessage}
              disabled={content.length === 0 || isSending}
              style={{ backgroundColor: '#C6A467' }}
            >
              {isSending ? <Loader size={16} color="white" /> : <IconSend size={20} />}
            </ActionIcon>
          </Flex>
        </Box>
      </Box>
    </Card>
  );
}
